/**
 * ISSUE-1381. The MLB pitchers block on the Matchup tab, after v1's
 * mlb_pitchers_block.dart. The server picks the phase, the title and every
 * line; this card lays them out:
 *   Starting pitchers  each side's announced starter, away first, or
 *                      "Not announced";
 *   Current pitchers   each side's pitcher on the mound, with this game's
 *                      line and pitch count;
 *   Pitching decisions the win, the loss and the save, each tagged W, L or S
 *                      with the record after it and the game line.
 * The team mark names the side. A row whose pitcher the server resolved to a
 * player opens the player page, like the Top performers rows. Nothing is
 * computed here.
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import type { typePlayerSearch } from "../../routeState/playerSearch";
import { GameSectionCard } from "./GameSectionCard";
import type {
  typeMatchupTeamMark,
  typePitcherRow,
  typePitchers,
} from "./matchupTypes";
import { TeamMark } from "./TeamMark";

export const PITCHER_NOT_ANNOUNCED = "Not announced";

const DECISION_CLASSES: Record<"W" | "L" | "S", string> = {
  W: "bg-success-tint text-success",
  L: "bg-error-tint text-error",
  S: "bg-primary-tint text-primary",
};

const DECISION_NAMES: Record<"W" | "L" | "S", string> = {
  W: "Win",
  L: "Loss",
  S: "Save",
};

const PitcherRow = ({
  row,
  team,
}: {
  row: typePitcherRow;
  team: typeMatchupTeamMark;
}): ReactNode => {
  const name = row.name ?? null;
  const tag = row.decisionTag ?? null;
  const record = row.recordLabel ?? null;
  const line = row.lineLabel ?? null;
  const content = (
    <>
      <TeamMark
        name={team.name}
        abbreviation={team.plateText}
        logoUrl={team.logoUrl}
        size="sm"
      />
      {tag !== null ? (
        <span
          data-testid="pitcher-decision"
          aria-label={DECISION_NAMES[tag]}
          className={cn(
            "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-caption font-bold",
            DECISION_CLASSES[tag],
          )}
        >
          {tag}
        </span>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        {name === null ? (
          <span
            data-testid="pitcher-not-announced"
            className="text-body text-ink-secondary"
          >
            <span className="sr-only">{team.name}: </span>
            {PITCHER_NOT_ANNOUNCED}
          </span>
        ) : (
          <span className="text-body-strong text-ink">
            <span className="sr-only">{team.name}: </span>
            {name}
            {record !== null ? (
              <span
                data-testid="pitcher-record"
                className="ms-2 text-small font-normal text-ink-secondary"
              >
                ({record})
              </span>
            ) : null}
          </span>
        )}
        {line !== null ? (
          <span
            data-testid="pitcher-line"
            className="text-small text-ink-secondary tabular-nums"
          >
            {line}
          </span>
        ) : null}
      </div>
    </>
  );
  const rowClass =
    "flex min-h-touch-target items-center gap-3 border-t border-hairline py-2 first:border-t-0";
  const playerId = row.playerId ?? "";
  if (
    name !== null &&
    playerId !== "" &&
    team.teamId !== undefined &&
    team.abbreviation !== undefined
  ) {
    const search: typePlayerSearch = {
      playerId,
      name,
      teamId: team.teamId,
      teamName: team.name,
      teamAbbreviation: team.abbreviation,
    };
    return (
      <div data-testid="pitcher-row" data-side={row.side} className={rowClass}>
        <Link
          to="/scores/player"
          search={search}
          data-testid="pitcher-player-link"
          className={`flex min-w-0 flex-1 items-center gap-3 text-ink no-underline ${FOCUS_RING}`}
        >
          {content}
        </Link>
      </div>
    );
  }
  return (
    <div data-testid="pitcher-row" data-side={row.side} className={rowClass}>
      {content}
    </div>
  );
};

export const PitchersSection = ({
  title,
  pitchers,
  away,
  home,
}: {
  title: string;
  pitchers: typePitchers;
  away: typeMatchupTeamMark;
  home: typeMatchupTeamMark;
}): ReactNode => (
  <GameSectionCard
    testId="pitchers-section"
    title={title}
    dataAttributes={{ "data-phase": pitchers.phase }}
  >
    <div className="flex flex-col">
      {pitchers.rows.map((row) => (
        <PitcherRow
          key={row.key}
          row={row}
          team={row.side === "home" ? home : away}
        />
      ))}
    </div>
  </GameSectionCard>
);
